import React, { useEffect, useState, useRef } from "react";
import { Card, ButtonGroup, Button } from "react-bootstrap";
import { Bar, Line } from "react-chartjs-2";
import { motion, AnimatePresence } from "framer-motion";
import { FaClock, FaCalendarAlt, FaTimes, FaChevronLeft, FaChevronRight, FaChevronDown, FaChartBar, FaChartLine } from "react-icons/fa";

import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend, Filler } from "chart.js";

import { fetchTicketsByAge } from "../../api/overviewApi";
import { useDataCache } from "../../contexts/DataCacheContext";

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend, Filler);

const CalendarRangePicker = ({ onDateSelect, onClose }) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedStart, setSelectedStart] = useState(null);
  const [selectedEnd, setSelectedEnd] = useState(null);
  const [selectingStart, setSelectingStart] = useState(true);
  const minDate = new Date(today);
  minDate.setDate(minDate.getDate() - 90);
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const dayNames = ["M", "T", "W", "T", "F", "S", "S"];

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    let startDay = firstDay.getDay() - 1;
    if (startDay < 0) startDay = 6;
    const days = [];
    const prevMonth = new Date(year, month, 0);
    for (let i = startDay - 1; i >= 0; i--) {
      days.push({ day: prevMonth.getDate() - i, isCurrentMonth: false, date: new Date(year, month - 1, prevMonth.getDate() - i) });
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ day: i, isCurrentMonth: true, date: new Date(year, month, i) });
    }
    const remaining = 35 - days.length;
    for (let i = 1; i <= Math.max(0, remaining); i++) {
      days.push({ day: i, isCurrentMonth: false, date: new Date(year, month + 1, i) });
    }
    return days.slice(0, 35);
  };

  const days = getDaysInMonth(currentMonth);
  const isDateInRange = (date) => date >= minDate && date <= today;
  const isSelected = (date) => {
    if (!selectedStart && !selectedEnd) return false;
    if (selectedStart && date.getTime() === selectedStart.getTime()) return true;
    if (selectedEnd && date.getTime() === selectedEnd.getTime()) return true;
    if (selectedStart && selectedEnd) return date >= selectedStart && date <= selectedEnd;
    return false;
  };
  const isInRange = (date) => {
    if (!selectedStart || !selectedEnd) return false;
    return date > selectedStart && date < selectedEnd;
  };

  const handleDateClick = (date) => {
    if (!isDateInRange(date)) return;
    if (selectingStart || (!selectedStart && !selectedEnd)) {
      setSelectedStart(date);
      setSelectedEnd(null);
      setSelectingStart(false);
    } else {
      if (date < selectedStart) {
        setSelectedStart(date);
        setSelectedEnd(selectedStart);
      } else {
        setSelectedEnd(date);
      }
    }
  };

  const handleApply = () => {
    if (selectedStart && selectedEnd) {
      onDateSelect(selectedStart, selectedEnd);
    } else if (selectedStart) {
      onDateSelect(selectedStart, selectedStart);
    }
  };

  const isDarkMode = document.body.classList.contains('dark-mode');
  const prevMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1);
  const prevMonthEnd = new Date(prevMonth.getFullYear(), prevMonth.getMonth() + 1, 0);
  const canGoPrev = prevMonthEnd >= minDate;
  const nextMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1);
  const nextMonthStart = new Date(nextMonth.getFullYear(), nextMonth.getMonth(), 1);
  const canGoNext = nextMonthStart <= today;

  return (
    <motion.div
      initial={{ opacity: 0, y: -5, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -5, scale: 0.98 }}
      transition={{ duration: 0.12 }}
      style={{
        position: "absolute",
        top: "calc(100% + 6px)",
        right: 0,
        zIndex: 1000,
        background: isDarkMode ? "#1e2139" : "#fff",
        borderRadius: 12,
        boxShadow: isDarkMode ? "0 8px 30px rgba(0,0,0,0.4)" : "0 8px 30px rgba(0,0,0,0.12)",
        padding: "10px",
        width: 240,
        border: isDarkMode ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.06)",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div style={{ fontSize: 11, fontWeight: 600, color: isDarkMode ? "#a0a4b8" : "#6b7280", marginBottom: 8, paddingBottom: 6, borderBottom: isDarkMode ? "1px solid rgba(255,255,255,0.1)" : "1px solid #f3f4f6" }}>
        Select Date Range
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
        <button onClick={() => canGoPrev && setCurrentMonth(prevMonth)} disabled={!canGoPrev} style={{ background: "none", border: "none", cursor: canGoPrev ? "pointer" : "not-allowed", padding: 3, borderRadius: 4, color: canGoPrev ? (isDarkMode ? "#a0a4b8" : "#6b7280") : (isDarkMode ? "#4a4f63" : "#d1d5db"), display: "flex", alignItems: "center", opacity: canGoPrev ? 1 : 0.3 }}>
          <FaChevronLeft size={9} />
        </button>
        <span style={{ fontWeight: 600, fontSize: 11, color: isDarkMode ? "#e2e4e9" : "#1f2937" }}>{monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}</span>
        <button onClick={() => canGoNext && setCurrentMonth(nextMonth)} disabled={!canGoNext} style={{ background: "none", border: "none", cursor: canGoNext ? "pointer" : "not-allowed", padding: 3, borderRadius: 4, color: canGoNext ? (isDarkMode ? "#a0a4b8" : "#6b7280") : (isDarkMode ? "#4a4f63" : "#d1d5db"), display: "flex", alignItems: "center", opacity: canGoNext ? 1 : 0.3 }}>
          <FaChevronRight size={9} />
        </button>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 1, marginBottom: 3 }}>
        {dayNames.map((day, i) => (
          <div key={i} style={{ textAlign: "center", fontSize: 8, fontWeight: 600, color: isDarkMode ? "#6b7280" : "#9ca3af", padding: "1px 0" }}>{day}</div>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 1 }}>
        {days.map((dayObj, i) => {
          const dateInRange = isDateInRange(dayObj.date);
          const selected = isSelected(dayObj.date);
          const inRange = isInRange(dayObj.date);
          const disabled = !dayObj.isCurrentMonth || !dateInRange;
          return (
            <button
              key={i}
              onClick={() => handleDateClick(dayObj.date)}
              disabled={disabled}
              style={{
                width: 26, height: 26, borderRadius: 5, border: selected ? "2px solid #8b5cf6" : "1px solid transparent",
                background: selected ? "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)" : inRange ? (isDarkMode ? "rgba(139, 92, 246, 0.15)" : "rgba(139, 92, 246, 0.1)") : "transparent",
                color: selected ? "#fff" : disabled ? (isDarkMode ? "#4a4f63" : "#d1d5db") : (isDarkMode ? "#e2e4e9" : "#374151"),
                fontSize: 10, fontWeight: selected ? 600 : 400, cursor: disabled ? "default" : "pointer", transition: "all 0.12s ease",
                display: "flex", alignItems: "center", justifyContent: "center", boxSizing: "border-box", padding: 0, margin: 0, lineHeight: 1, textAlign: "center",
              }}
            >
              {dayObj.day}
            </button>
          );
        })}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 6, marginTop: 10, paddingTop: 10, borderTop: isDarkMode ? "1px solid rgba(255,255,255,0.1)" : "1px solid #f3f4f6" }}>
        <button onClick={onClose} style={{ padding: "5px 10px", borderRadius: 5, border: "none", background: "transparent", color: isDarkMode ? "#a0a4b8" : "#6b7280", fontSize: 10, fontWeight: 500, cursor: "pointer" }}>Cancel</button>
        <button onClick={handleApply} disabled={!selectedStart || !selectedEnd} style={{ padding: "5px 10px", borderRadius: 5, border: "none", background: (selectedStart && selectedEnd) ? "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)" : (isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"), color: (selectedStart && selectedEnd) ? "#fff" : (isDarkMode ? "#6b7280" : "#9ca3af"), fontSize: 10, fontWeight: 600, cursor: (selectedStart && selectedEnd) ? "pointer" : "not-allowed", opacity: (selectedStart && selectedEnd) ? 1 : 0.5 }}>Apply</button>
      </div>
    </motion.div>
  );
};

const TicketAgeChart = ({ className, clientId }) => {
  const { getCachedData, setCachedData } = useDataCache();
  const getDefaultDateRange = () => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 7);
    return { start, end };
  };
  const [chartMode, setChartMode] = useState("bar");
  const [chartData, setChartData] = useState(null);
  const [dateRange, setDateRange] = useState(() => getDefaultDateRange());
  const [showCalendar, setShowCalendar] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const canvasRef = useRef(null);
  const calendarRef = useRef(null);
  const isInitialLoad = useRef(true);

  const isDark = document.body.classList.contains("dark-mode");

  const formatDateForDisplay = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
  };

  const formatDateRangeForDisplay = (start, end) => {
    if (!start || !end) return "";
    return `${formatDateForDisplay(start)} - ${formatDateForDisplay(end)}`;
  };

  const formatDateForApi = (date) => {
    if (!date) return null;
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const clearDateRange = (e) => {
    if (e) e.stopPropagation();
    setDateRange(getDefaultDateRange());
    setShowCalendar(false);
  };

  const handleDateRangeSelect = (start, end) => {
    setDateRange({ start, end });
    setShowCalendar(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setShowCalendar(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!clientId) return;

    const startApi = formatDateForApi(dateRange.start);
    const endApi = formatDateForApi(dateRange.end);
    const defaultColors = ["#36b9cc", "#4e73df", "#6f42c1", "#f59e0b", "#10b981", "#6b7280"];
    const cacheKey = `tickets_by_age_${startApi}_${endApi}`;
    const cached = getCachedData(cacheKey, clientId);

    if (cached !== undefined) {
      const safeData = {
        labels: cached.labels || ["0-7", "8-14", "15-30", "31-60", "61-90", "90+"],
        values: cached.values || [0, 0, 0, 0, 0, 0],
        colors: cached.colors || defaultColors,
      };
      setChartData(safeData);
      setIsRefetching(false);
      isInitialLoad.current = false;
      return;
    }

    if (isInitialLoad.current) {
      const overviewCached = getCachedData("overview_tickets_by_age", clientId);
      if (overviewCached !== undefined) {
        const colors = ["#36b9cc", "#4e73df", "#6f42c1", "#f59e0b", "#10b981", "#6b7280"];
        const data = {
          labels: overviewCached.labels || ["0-7", "8-14", "15-30", "31-60", "61-90", "90+"],
          values: overviewCached.values || [0, 0, 0, 0, 0, 0],
          colors,
        };
        setChartData(data);
        setCachedData(cacheKey, data, clientId);
        setIsRefetching(false);
        isInitialLoad.current = false;
        return;
      }
    }

    if (!isInitialLoad.current) {
      setIsRefetching(true);
    }

    const abortController = new AbortController();

    fetchTicketsByAge(clientId, startApi, endApi, abortController.signal)
      .then((res) => {
        const colors = ["#36b9cc", "#4e73df", "#6f42c1", "#f59e0b", "#10b981", "#6b7280"];
        const labels = res.labels && res.labels.length > 0 ? res.labels : ["0-7", "8-14", "15-30", "31-60", "61-90", "90+"];
        const values = res.values && res.values.length > 0 ? res.values : [0, 0, 0, 0, 0, 0];
        const data = { labels, values, colors };
        setChartData(data);
        setCachedData(cacheKey, data, clientId);
        setIsRefetching(false);
        isInitialLoad.current = false;
      })
      .catch((err) => {
        if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') return;
        setChartData({
          labels: ["0-7", "8-14", "15-30", "31-60", "61-90", "90+"],
          values: [0, 0, 0, 0, 0, 0],
          colors: ["#36b9cc", "#4e73df", "#6f42c1", "#f59e0b", "#10b981", "#6b7280"],
        });
        setIsRefetching(false);
        isInitialLoad.current = false;
      });

    return () => abortController.abort();
  }, [clientId, dateRange, getCachedData, setCachedData]);

  if (!chartData) {
    return (
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }} className={className}>
        <Card className="shadow-sm" style={{ borderRadius: 16, background: "var(--color-bg-card)", color: "var(--color-text)", border: "1px solid var(--color-border)" }}>
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-4 mt-2">
              <h6 className="fw-bold d-flex align-items-center gap-2 mb-0" style={{ color: "var(--color-text)" }}>
                <div style={{ width: 32, height: 32, borderRadius: 10, background: "rgba(239,68,68,0.15)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <FaClock size={16} color="#ef4444" />
                </div>
                Tickets by Days Open
              </h6>
            </div>
            <div style={{ minHeight: "47dvh", display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
              <div style={{ width: 16, height: 16, borderRadius: "50%", border: "2px solid rgba(139, 92, 246, 0.3)", borderTopColor: "#8b5cf6", animation: "spin 0.8s linear infinite" }} />
              <span style={{ color: "var(--color-text-light)", fontSize: 13 }}>Loading...</span>
            </div>
          </Card.Body>
        </Card>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </motion.div>
    );
  }

  const barData = {
    labels: chartData.labels,
    datasets: [{
      label: "Open Tickets",
      data: chartData.values,
      backgroundColor: chartData.colors.map((c) => c + "CC"),
      borderColor: chartData.colors,
      borderWidth: 2,
      borderRadius: 8,
      barThickness: 26,
    }],
  };

  const getAxisTextColor = () => isDark ? '#a0a4b8' : '#6c737f';

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "#000000",
        titleColor: "#ffffff",
        bodyColor: "#ffffff",
        borderColor: "#000000",
        borderWidth: 0,
        padding: 10,
        displayColors: true,
        callbacks: {
          labelColor: function(context) {
            const barColor = chartData.colors[context.dataIndex] || "#000000";
            return { borderColor: barColor, backgroundColor: barColor };
          },
          label: function(context) {
            // ── FIX: use nullish coalescing so 0 is shown correctly ──
            const value = context.parsed.y ?? 0;
            return `Open Tickets: ${value}`;
          },
        },
      },
    },
    scales: {
      x: {
        ticks: { color: getAxisTextColor() },
        grid: { display: false },
      },
      y: {
        beginAtZero: true,
        min: 0,
        suggestedMax: Math.max(...chartData.values, 5),
        ticks: {
          color: getAxisTextColor(),
          stepSize: Math.max(...chartData.values) > 50 ? 25 : (Math.max(...chartData.values) > 10 ? 5 : 1),
          callback: function(value) { if (Math.floor(value) === value) return value; },
        },
        grid: {
          display: true,
          color: isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.05)",
          drawBorder: false,
        },
      },
    },
  };

  const areaData = {
    labels: chartData.labels,
    datasets: [{
      label: "Open Tickets",
      data: chartData.values,
      borderColor: "#6f42c1",
      borderWidth: 2,
      fill: true,
      backgroundColor: (ctx) => {
        const gradient = ctx.chart.ctx.createLinearGradient(0, 0, 0, 260);
        gradient.addColorStop(0, isDark ? "rgba(111, 66, 193, 0.45)" : "rgba(111, 66, 193, 0.35)");
        gradient.addColorStop(1, "rgba(111, 66, 193, 0.05)");
        return gradient;
      },
      tension: 0.45,
      pointRadius: 4,
      pointHoverRadius: 8,
      pointBackgroundColor: "#6f42c1",
      pointBorderColor: "#ffffff",
      pointBorderWidth: 2,
      pointHoverBackgroundColor: "#6f42c1",
      pointHoverBorderColor: "#ffffff",
      pointHoverBorderWidth: 3,
    }],
  };

  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "#000000",
        titleColor: "#ffffff",
        bodyColor: "#ffffff",
        borderColor: "#000000",
        borderWidth: 0,
        padding: 10,
        displayColors: true,
        callbacks: {
          labelColor: function() {
            return { borderColor: "#6f42c1", backgroundColor: "#6f42c1" };
          },
          label: function(context) {
            // ── FIX: use nullish coalescing so 0 is shown correctly ──
            const value = context.parsed.y ?? 0;
            return `Open Tickets: ${value}`;
          },
        },
      },
    },
    scales: {
      x: {
        ticks: { color: getAxisTextColor() },
        grid: { display: false },
      },
      y: {
        beginAtZero: true,
        min: 0,
        suggestedMax: Math.max(...chartData.values, 5),
        ticks: {
          color: getAxisTextColor(),
          stepSize: Math.max(...chartData.values) > 50 ? 25 : (Math.max(...chartData.values) > 10 ? 5 : 1),
          callback: function(value) { if (Math.floor(value) === value) return value; },
        },
        grid: {
          display: true,
          color: isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.05)",
          drawBorder: false,
        },
      },
    },
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }} className={className}>
      <Card className="shadow-sm" style={{ borderRadius: 16, background: "var(--color-bg-card)", color: "var(--color-text)", border: "1px solid var(--color-border)" }}>
        <Card.Body>
          {/* HEADER */}
          <div className="d-flex justify-content-between align-items-center mb-4 mt-2">
            <h6 className="fw-bold d-flex align-items-center gap-2 mb-0" style={{ color: "var(--color-text)" }}>
              <div style={{ width: 32, height: 32, borderRadius: 10, background: "rgba(239,68,68,0.15)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <FaClock size={16} color="#ef4444" />
              </div>
              Tickets by Days Open
            </h6>

            <div style={{ display: "flex", flexWrap: "nowrap", gap: 6, alignItems: "center", justifyContent: "flex-end", flexShrink: 0, minWidth: 0 }}>
              <div className="position-relative" ref={calendarRef} style={{ flexShrink: 1, minWidth: 0 }}>
                <div
                  onClick={() => setShowCalendar(!showCalendar)}
                  style={{
                    borderRadius: 8, background: "var(--color-bg-card)",
                    border: dateRange?.start && dateRange?.end ? "1px solid rgba(139, 92, 246, 0.4)" : "1px solid var(--color-border)",
                    padding: "4px 8px", fontSize: 11, cursor: "pointer",
                    maxWidth: 240, minWidth: 140, display: "flex", alignItems: "center", gap: 6,
                    boxShadow: "0 2px 4px rgba(0,0,0,0.08)", transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = "rgba(139, 92, 246, 0.5)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = (dateRange?.start && dateRange?.end) ? "rgba(139, 92, 246, 0.4)" : "var(--color-border)"; }}
                >
                  <FaCalendarAlt size={10} color="#8b5cf6" style={{ flexShrink: 0 }} />
                  <span style={{ color: (dateRange?.start && dateRange?.end) ? "#a78bfa" : "var(--color-text)", fontWeight: 500, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {dateRange?.start && dateRange?.end ? formatDateRangeForDisplay(dateRange.start, dateRange.end) : "Date Range"}
                  </span>
                  {dateRange?.start && dateRange?.end ? (
                    <div
                      onClick={(e) => { e.stopPropagation(); clearDateRange(e); }}
                      style={{ width: 18, height: 18, borderRadius: "50%", background: "rgba(239, 68, 68, 0.15)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", transition: "background 0.15s ease", flexShrink: 0 }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(239, 68, 68, 0.25)"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(239, 68, 68, 0.15)"; }}
                    >
                      <FaTimes size={8} color="#dc2626" />
                    </div>
                  ) : (
                    <FaChevronDown size={9} style={{ color: "var(--color-text-light)", transform: showCalendar ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s ease", flexShrink: 0 }} />
                  )}
                </div>
                <AnimatePresence>{showCalendar && <CalendarRangePicker onDateSelect={handleDateRangeSelect} onClose={() => setShowCalendar(false)} />}</AnimatePresence>
              </div>

              <div
                role="switch"
                aria-label={`Chart type: ${chartMode}`}
                tabIndex={0}
                onClick={() => setChartMode((prev) => (prev === "bar" ? "area" : "bar"))}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setChartMode((prev) => (prev === "bar" ? "area" : "bar"));
                  }
                }}
                style={{
                  display: "inline-flex", alignItems: "center",
                  width: 70, minWidth: 70, height: 28, borderRadius: 14,
                  background: isDark
                    ? "linear-gradient(90deg, rgba(78, 115, 223, 0.35) 0%, rgba(78, 115, 223, 0.55) 100%)"
                    : "linear-gradient(90deg, #93b5f9 0%, #5a7fe8 50%, #4e73df 100%)",
                  border: "none", position: "relative", cursor: "pointer", padding: 2,
                  boxShadow: isDark ? "0 3px 10px rgba(0,0,0,0.3)" : "0 3px 12px rgba(78, 115, 223, 0.35), inset 0 1px 0 rgba(255,255,255,0.15)",
                  flexShrink: 0,
                }}
              >
                <motion.div layout initial={false} animate={{ x: chartMode === "bar" ? 0 : 42 }} transition={{ type: "spring", stiffness: 400, damping: 28 }}
                  style={{ position: "absolute", left: 2, top: 2, width: 24, height: 24, borderRadius: "50%", background: isDark ? "var(--color-bg-card)" : "#ffffff", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: isDark ? "0 2px 8px rgba(0,0,0,0.4)" : "0 2px 6px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.9)" }}>
                  <motion.span key={chartMode} initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.2 }} style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {chartMode === "bar" ? <FaChartBar size={10} color={isDark ? "#93b5f9" : "#4e73df"} /> : <FaChartLine size={10} color={isDark ? "#93b5f9" : "#4e73df"} />}
                  </motion.span>
                </motion.div>
                <span style={{ position: "relative", zIndex: 1, width: "50%", textAlign: "center", fontSize: 12, fontWeight: 600, color: chartMode === "bar" ? "transparent" : "#ffffff", letterSpacing: "0.02em" }}>Area</span>
                <span style={{ position: "relative", zIndex: 1, width: "50%", textAlign: "center", fontSize: 12, fontWeight: 600, color: chartMode === "area" ? "transparent" : "#ffffff", letterSpacing: "0.02em" }}>Bar</span>
              </div>
            </div>
          </div>

          {/* CHART */}
          <div style={{ minHeight: "47dvh", position: "relative" }}>
            <AnimatePresence>
              {isRefetching && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}
                  style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
                  <div style={{ width: 16, height: 16, borderRadius: "50%", border: "2px solid rgba(139, 92, 246, 0.3)", borderTopColor: "#8b5cf6", animation: "spin 0.8s linear infinite" }} />
                  <span style={{ color: "var(--color-text-light)", fontSize: 13 }}>Loading...</span>
                </motion.div>
              )}
            </AnimatePresence>
            {chartMode === "bar" ? <Bar data={barData} options={barOptions} /> : <Line ref={canvasRef} data={areaData} options={lineOptions} />}
          </div>
        </Card.Body>
      </Card>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </motion.div>
  );
};

export default TicketAgeChart;